import React, {useContext} from 'react';
import Tooltip, {TooltipProps} from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import theme from '../../theme';
import trackAnalytics from '../../utils/gaia/trackAnalytics';
import {makeStyles} from '@material-ui/core/styles';
import useUser from '../../hooks/useUser';
import {omit} from 'lodash';
import AuthModalContext from './contexts/AuthModalContext';
import {popupZIndex} from '../../utils/gaia/zIndicies';

const useStyles = makeStyles({
    popper: {
        zIndex: popupZIndex + 1,
    },
});

export type SubscriptionTooltipProps = Partial<TooltipProps> &
    (
        | {
              subscription: 'user' | 'member' | 'gaiagreen' | 'gaiafree';
              analyticsContext: string;
              children: TooltipProps['children'];
          }
        | {
              subscription: undefined;
              analyticsContext?: string;
              children: TooltipProps['children'];
          }
    );

export default function SubscriptionTooltip(props: SubscriptionTooltipProps) {
    const [user] = useUser();

    if (props.subscription === 'user' && user && !user.isAuthenticated) {
        return (
            <Inner
                title={props.title}
                subscriptionHref='/register/'
                subscriptionTitle='Sign up for a free account to use this feature'
                {...omit({...props}, 'subscription')}
            />
        );
    } else if (
        props.subscription === 'member' &&
        user &&
        !user.hasSubscription
    ) {
        return (
            <Inner
                title={props.title}
                subscriptionHref='/membership/'
                subscriptionTitle='Become a Member to use this feature'
                {...omit({...props}, 'subscription')}
            />
        );
    } else if (
        props.subscription === 'gaiagreen' &&
        user &&
        !user.hasPremiumSubscription
    ) {
        return (
            <Inner
                title={props.title}
                subscriptionHref='/premium/'
                subscriptionTitle='Become a Premium Member to use this feature'
                {...omit({...props}, 'subscription')}
            />
        );
    } else if (props.title) {
        return (
            <Tooltip
                title={
                    <div style={{padding: theme.spacing(1)}}>{props.title}</div>
                }
                {...omit({...props}, 'title', 'analyticsContext')}
            />
        );
    } else {
        return props.children;
    }
}

interface InnerProps extends TooltipProps {
    subscriptionTitle: string;
    subscriptionHref: string;
    analyticsContext: string;
}

const buttonStyle = {
    fontSize: 12,
    color: '#fff',
    marginTop: theme.spacing(2),
};

function Inner({
    subscriptionTitle,
    subscriptionHref,
    title,
    analyticsContext,
    ...restProps
}: InnerProps) {
    const [user] = useUser();
    const setAuthModal = useContext(AuthModalContext);
    const classes = useStyles();
    const onUserAuthenticated = () => {
        if (setAuthModal) setAuthModal({form: undefined, analyticsContext});
    };

    return (
        <Tooltip
            {...restProps}
            classes={{popper: classes.popper}}
            interactive
            leaveDelay={100}
            title={
                <Typography
                    component='div'
                    variant='body1'
                    color='inherit'
                    style={{padding: theme.spacing(1)}}
                >
                    <div style={{fontWeight: 'bold'}}>{title}</div>

                    {subscriptionTitle}
                    <div>
                        {user && user.isAuthenticated ? (
                            <Button
                                style={buttonStyle}
                                variant='contained'
                                color='primary'
                                href={`${subscriptionHref}?next=${window.location.pathname}`}
                                onClick={() =>
                                    trackAnalytics(
                                        'Subscription Tooltip Upgrade',
                                        {
                                            category: 'map2',
                                            context: analyticsContext,
                                        }
                                    )
                                }
                            >
                                Upgrade
                            </Button>
                        ) : (
                            <>
                                <Button
                                    style={{
                                        ...buttonStyle,
                                        color: theme.palette.primary.main,
                                        marginRight: theme.spacing(2),
                                    }}
                                    variant='outlined'
                                    color='primary'
                                    onClick={() => {
                                        trackAnalytics(
                                            'Subscription Tooltip Log In',
                                            {
                                                category: 'map2',
                                                context: analyticsContext,
                                            }
                                        );
                                        if (setAuthModal) {
                                            setAuthModal({
                                                form: 'login',
                                                onUserAuthenticated,
                                                analyticsContext,
                                            });
                                        }
                                    }}
                                >
                                    Log In
                                </Button>
                                <Button
                                    style={buttonStyle}
                                    variant='contained'
                                    color='primary'
                                    onClick={() => {
                                        trackAnalytics(
                                            'Subscription Tooltip Sign Up',
                                            {
                                                category: 'map2',
                                                context: analyticsContext,
                                            }
                                        );
                                        if (setAuthModal) {
                                            setAuthModal({
                                                form: 'register',
                                                onUserAuthenticated,
                                                analyticsContext,
                                            });
                                        }
                                    }}
                                >
                                    Sign Up
                                </Button>
                            </>
                        )}
                    </div>
                </Typography>
            }
        />
    );
}
